import { useMemo, useState } from "react";
import {
  EMAIL_TEMPLATES,
  renderTemplate,
  templateVariables,
} from "./emailTemplateData";

export default function EmailTemplatePage() {
  const [selectedId, setSelectedId] = useState(EMAIL_TEMPLATES[0].id);

  const selected = useMemo(
    () => EMAIL_TEMPLATES.find((t) => t.id === selectedId) ?? EMAIL_TEMPLATES[0],
    [selectedId]
  );

  const previewHtml = useMemo(() => renderTemplate(selected), [selected]);
  const variables = useMemo(() => templateVariables(selected), [selected]);

  return (
    <div className="flex-1 flex flex-col gap-8">
      {/* Header */}
      <header>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Email Templates
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Preview of every transactional email the platform sends, rendered with
          sample data.
        </p>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row gap-6">
        {/* Template list */}
        <aside className="lg:w-72 flex-shrink-0">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
            <ul className="divide-y divide-gray-100 dark:divide-gray-800">
              {EMAIL_TEMPLATES.map((t) => {
                const active = t.id === selectedId;
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(t.id)}
                      className={`w-full text-left px-4 py-3 transition-colors ${
                        active
                          ? "bg-blue-50 dark:bg-blue-950/40 border-l-4 border-[#1E40AF]"
                          : "border-l-4 border-transparent hover:bg-gray-50 dark:hover:bg-gray-800"
                      }`}
                    >
                      <div
                        className={`text-sm font-semibold ${
                          active
                            ? "text-[#1E40AF] dark:text-blue-300"
                            : "text-gray-900 dark:text-white"
                        }`}
                      >
                        {t.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {t.description}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        {/* Preview */}
        <section className="flex-1 flex flex-col gap-4 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {selected.name}
              </h2>
              <code className="text-xs text-gray-500 dark:text-gray-400">
                {selected.id}.html
              </code>
            </div>
            {variables.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Variables:
                </span>
                {variables.map((v) => (
                  <span
                    key={v}
                    className="rounded-md bg-gray-100 dark:bg-gray-800 px-2 py-1 text-xs font-mono text-gray-700 dark:text-gray-300"
                  >
                    #{v}#
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-950 overflow-hidden">
            <iframe
              key={selected.id}
              title={`${selected.name} preview`}
              srcDoc={previewHtml}
              className="w-full h-[720px] border-0 bg-white"
              sandbox=""
            />
          </div>
        </section>
      </div>
    </div>
  );
}
